import { createError } from '@common/neo.tax';

import { GitHubConnectMethod } from '@neo.tax/shared/enums';
import { AccountingProvider, PayrollProvider, TicketingProvider } from '@neo.tax/shared/graphql';
import { isFinchProvider } from '@neo.tax/shared/third-parties';

import {
  ConnectPayroll,
  ConnectFinch,
  ConnectAccounting,
  ConnectCsvProvider,
  ConnectTicketing,
  ConnectGitHub,
  ConnectRdPayrollCostProvider,
  ConnectAccountingProviderSlideProps,
  ConnectPayrollProviderSlideProps,
  ConnectTicketingProviderSlideProps,
} from './slides';

type ConnectProviderSlideProps = {
  provider: ConnectAccountingProviderSlideProps | ConnectPayrollProviderSlideProps | ConnectTicketingProviderSlideProps;
  close: () => void;
  restart: () => void;
  fiscalYearId: string;
  /** Skips GitHub's method-choice step; see ConnectGitHub's `initialMethod`. */
  initialGitHubConnectMethod?: GitHubConnectMethod | null;
  /** The organization a resumed or reconnected GitHub App connection is already for. */
  initialOrganizationSlug?: string | null;
};

export const ConnectProviderSlide = ({
  close,
  fiscalYearId,
  initialGitHubConnectMethod,
  initialOrganizationSlug,
  provider,
  restart,
}: ConnectProviderSlideProps): React.ReactElement | null => {
  const { providerName, providerType } = provider;
  switch (providerType) {
    case 'payroll': {
      if (providerName === PayrollProvider.Gusto) {
        return (
          <ConnectPayroll close={close} fiscalYearId={fiscalYearId} restart={restart} providerName={providerName} />
        );
      }
      if ([PayrollProvider.Rippling, PayrollProvider.SelfReported].includes(providerName)) {
        return (
          <ConnectRdPayrollCostProvider
            fiscalYearId={fiscalYearId}
            close={close}
            restart={restart}
            providerName={providerName}
            providerType="payroll"
          />
        );
      }
      if (isFinchProvider(providerName)) {
        return <ConnectFinch close={close} fiscalYearId={fiscalYearId} restart={restart} providerName={providerName} />;
      }
      throw createError(
        'UnsupportedPayrollProvider',
        `Unknown payroll provider ${providerName} in ConnectProviderSlide`,
      );
    }
    case 'accounting': {
      if (providerName === AccountingProvider.SelfReported) {
        return (
          <ConnectCsvProvider
            close={close}
            fiscalYearId={fiscalYearId}
            restart={restart}
            providerName={providerName}
            providerType="accounting"
          />
        );
      }
      return (
        <ConnectAccounting close={close} fiscalYearId={fiscalYearId} restart={restart} providerName={providerName} />
      );
    }
    case 'ticketing': {
      // GitHub supports two acquisition methods (direct GitHub App and the legacy PAT through
      // Merge — ADR 0002), so it gets a method-choice step first. Every other provider is
      // Merge-only and goes straight to that flow.
      if (providerName === TicketingProvider.GithubIssues) {
        return (
          <ConnectGitHub
            close={close}
            restart={restart}
            providerName={providerName}
            initialMethod={initialGitHubConnectMethod}
            initialOrganizationSlug={initialOrganizationSlug}
          />
        );
      }
      return <ConnectTicketing close={close} restart={restart} providerName={providerName} />;
    }
    default:
      return null;
  }
};
